import { useEffect, useMemo, useState } from 'react';
import { Zap, Shield, ArrowRight, CheckCircle2, Phone, MapPin, Mail, Globe, IndianRupee, Sun, Home as HomeIcon, Building2, Factory, Star, Wrench, Wind, ChevronDown, Menu, X, Battery, Cable, Layers, Award, HeartHandshake, Briefcase, LogIn, UserCircle2, ShieldCheck, UserPlus, Wheat, FileText, Landmark, HardHat, Smartphone, Download } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectTrigger, SelectContent, SelectItem, SelectValue } from '@/components/ui/select';
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuLabel, DropdownMenuSeparator } from '@/components/ui/dropdown-menu';
import { EmiCalculator } from '@/components/EmiCalculator';
import BookingDialog from '@/components/BookingDialog';
import CareerApplyDialog from '@/components/landing/CareerApplyDialog';
import { SiteHeader } from '@/components/site/SiteHeader';
import QuoteDialog from '@/components/site/QuoteDialog';
import { BankLogo } from '@/components/BankLogo';
import { api } from '@/lib/api';
import { systemCostForKw } from '@/lib/utils';
import LanguageToggle from '@/components/LanguageToggle';
import { useLang } from '@/i18n/LangContext';
import { extraApi } from '@/lib/extraApi';
import { toast } from 'sonner';
import OtpVerifyBlock from '@/components/OtpVerifyBlock';


const SOLUTIONS = [
    {
        key: 'residential',
        title: 'Residential Solar',
        subtitle: 'Rooftop for Homes',
        icon: HomeIcon,
        iconBg: 'bg-gradient-to-br from-sky-400 to-sky-600 text-white shadow-lg shadow-sky-500/30',
        cardTint: 'from-sky-50 to-white',
        emoji: '🏠',
        short: 'Cut your monthly bill by up to 90% with a rooftop solar system designed for Indian homes and PM Surya Ghar subsidy.',
        highlights: [
            'System sizes: 1 kW – 10 kW',
            'PM Surya Ghar subsidy up to ₹78,000',
            'Net metering with UPPCL / DISCOM',
            '25-year performance warranty',
            'Financing available: 0% down, easy EMI',
        ],
        savings: '₹1,500 – ₹8,000/month',
        payback: '3 – 5 years',
        idealFor: 'Independent houses, villas, farmhouses with 200+ sqft roof.',
    },
    {
        key: 'commercial',
        title: 'Commercial Solar',
        subtitle: 'Shops, Offices, Hotels',
        icon: Building2,
        iconBg: 'bg-gradient-to-br from-blue-500 to-indigo-600 text-white shadow-lg shadow-blue-500/30',
        cardTint: 'from-blue-50 to-white',
        emoji: '🏢',
        short: 'Reduce operating costs and demand charges. Business-grade solar systems from 10 kW to 500 kW with monitoring.',
        highlights: [
            'System sizes: 10 kW – 500 kW',
            '40% accelerated depreciation benefit',
            'Custom PPA & CAPEX models',
            'Remote monitoring dashboard',
            'ROI-driven design & load studies',
        ],
        savings: '₹40,000 – ₹5,00,000/month',
        payback: '4 – 6 years',
        idealFor: 'Retail chains, offices, warehouses, hotels, hospitals, schools.',
    },
    {
        key: 'industrial',
        title: 'Industrial Solar',
        subtitle: 'Factories & MSMEs',
        icon: Factory,
        iconBg: 'bg-gradient-to-br from-slate-600 to-slate-800 text-white shadow-lg shadow-slate-600/30',
        cardTint: 'from-slate-50 to-white',
        emoji: '🏭',
        short: 'Large-scale ground-mount or rooftop plants engineered to offset heavy industrial loads and lower cost per unit.',
        highlights: [
            'System sizes: 100 kW – 5 MW',
            'Group Captive & open access options',
            'IEC/BIS certified components',
            'SCADA + 3rd-party O&M available',
            'Green Financing partnerships',
        ],
        savings: 'Up to ₹1.5/unit vs grid',
        payback: '3 – 5 years',
        idealFor: 'Manufacturing units, cold storage, textile mills, dairies.',
    },
    {
        key: 'atta_chakki',
        title: 'Solar Atta Chakki',
        subtitle: 'Zero-electricity flour mill',
        icon: Wheat,
        iconBg: 'bg-gradient-to-br from-amber-400 to-orange-500 text-white shadow-lg shadow-amber-500/30',
        cardTint: 'from-amber-50 to-white',
        emoji: '🌾',
        short: 'Run your flour mill entirely on solar. Ideal for villages with power cuts — grind 40–120 kg/hr without a diesel bill.',
        highlights: [
            'Motor sizes: 3 HP – 15 HP',
            'Works on cloudy days with battery backup',
            'Fits in a 10x8 ft room',
            'Government subsidy up to 60% (state-wise)',
            'Ready-to-run turnkey package',
        ],
        savings: 'Save ₹8,000 – ₹25,000/month diesel',
        payback: '2 – 3 years',
        idealFor: 'Rural entrepreneurs, wheat/masala mills, aata-daliya makers.',
    },
];

export default function Landing() {
    const nav = useNavigate();
    const { t, lang } = useLang();
    const [quoteOpen, setQuoteOpen] = useState(false);
    const [quoteKw, setQuoteKw] = useState(null);   // pre-filled kW for slider CTA
    const [bookingOpen, setBookingOpen] = useState(false);
    const [bookingKw, setBookingKw] = useState(3);
    const [careers, setCareers] = useState([]);
    const [careerApply, setCareerApply] = useState(null);   // Phase 47.50 — Apply-dialog active career
    const [solutionOpen, setSolutionOpen] = useState(null);
    const [stories, setStories] = useState(null);   // CMS-driven success stories
    const [saathiOpen, setSaathiOpen] = useState(false);
    const [cms, setCms] = useState(null);           // Admin-editable homepage content (Hero / About / Contact)
    // Admin-editable per-capacity rate chart (system cost + subsidy + booking amount).
    // Falls back to a static hardcoded array only when the API fetch fails / returns empty.
    const [rateChart, setRateChart] = useState([]);
    useEffect(() => {
        api.subsidyRates()
            .then((rows) => setRateChart(Array.isArray(rows) ? rows.filter(r => r && r.kw) : []))
            .catch(() => setRateChart([]));
    }, []);
    const activeSolution = SOLUTIONS.find(s => s.key === solutionOpen);

    // Load success stories from CMS (category='story')
    useEffect(() => {
        fetch(`${import.meta.env?.REACT_APP_BACKEND_URL || process.env.REACT_APP_BACKEND_URL}/api/content`)
            .then(r => r.json())
            .then(d => setStories((d || []).filter(c => (c.category || '') === 'story')))
            .catch(() => setStories([]));
    }, []);

    // Load admin-editable landing content (Hero / About / Contact). Falls back to
    // hard-coded translations if the CMS is empty or the fetch fails.
    useEffect(() => {
        api.getLandingContent().then(setCms).catch(() => setCms(null));
    }, []);

    // Read a bilingual field with graceful fallback to `t()` translation key.
    // Usage:  cmsText('hero.badge', 'landing_scheme_badge')
    const cmsText = (path, fallbackKey) => {
        try {
            const node = path.split('.').reduce((o, k) => (o == null ? o : o[k]), cms);
            if (node && typeof node === 'object') {
                const val = node[lang] || node.en || node.hi;
                if (val && String(val).trim()) return val;
            } else if (typeof node === 'string' && node.trim()) {
                return node;
            }
        } catch (_) { /* ignore */ }
        return fallbackKey ? t(fallbackKey) : '';
    };
    // Read a plain (non-bilingual) CMS field, e.g. contact.phone.
    const cmsPlain = (path, fallback = '') => {
        try {
            const node = path.split('.').reduce((o, k) => (o == null ? o : o[k]), cms);
            if (typeof node === 'string' && node.trim()) return node;
        } catch (_) { /* ignore */ }
        return fallback;
    };

    const openQuoteWithKw = (kw) => { setQuoteKw(kw); setQuoteOpen(true); };


    useEffect(() => { extraApi.listCareers().then(setCareers).catch(() => setCareers([])); }, []);

    return (
        <div id='home' className='app-shell relative overflow-hidden'>
            <div className='hero-gradient absolute inset-0 -z-10' />

            <FestivalRibbon onQuote={() => setQuoteOpen(true)} />

            {/* Shared multi-page website header (mega-menu, language, quote, login) */}
            <SiteHeader onQuote={() => setQuoteOpen(true)} phone={cmsPlain('contact.phone')} />

            {/* Hero */}
            <section className='max-w-7xl mx-auto px-4 sm:px-8 pt-3 sm:pt-6 pb-5 sm:pb-10'>
                <div className='grid lg:grid-cols-2 gap-5 lg:gap-10 items-center'>
                    <div>
                        <Badge className='mb-2 sm:mb-4 bg-accent/15 text-accent border-accent/30 hover:bg-accent/20'>{cmsText('hero.badge', 'landing_scheme_badge')}</Badge>
                        <h1 className='font-display text-3xl sm:text-5xl lg:text-6xl font-semibold leading-[1.1] tracking-tight'>
                            {cmsText('hero.h1_a', 'landing_h1_a')}<br /><span className='text-accent'>{cmsText('hero.h1_b', 'landing_h1_b')}</span>
                        </h1>
                        <p className='mt-2 sm:mt-4 text-sm sm:text-lg text-muted-foreground max-w-xl'>{cmsText('hero.subtitle', 'landing_sub')}</p>
                        <div className='mt-3 sm:mt-6 flex flex-wrap gap-2 sm:gap-3'>
                            <Button size='lg' className='min-h-[44px] sm:min-h-[52px] bg-accent hover:bg-accent/90 text-accent-foreground' onClick={() => setQuoteOpen(true)} data-testid='landing-cta-apply'>
                                {cmsText('hero.cta_primary', 'cta_free_consultation')} <ArrowRight className='ml-2 w-4 h-4' />
                            </Button>
                            <Button size='lg' variant='outline' className='min-h-[44px] sm:min-h-[52px]' onClick={() => nav('/customer/login')} data-testid='landing-cta-status'>{cmsText('hero.cta_secondary', 'cta_check_status')}</Button>
                        </div>
                        <div className='mt-3 sm:mt-6 flex items-center gap-2 text-sm'>
                            <div className='flex items-center gap-1 text-amber-500'>{[1,2,3,4,5].map(i => <Star key={i} className='w-4 h-4 fill-current' />)}</div>
                            <span className='text-muted-foreground'>{t('rated')}</span>
                        </div>
                    </div>
                    <div className='space-y-3'>
                        {cms?.hero?.hero_image_url && (
                            <img src={cms.hero.hero_image_url} alt='' className='w-full h-32 sm:h-40 object-cover rounded-2xl shadow-md ring-1 ring-black/5' data-testid='landing-hero-image' />
                        )}
                        <EmiCalculator onBook={(info) => { setBookingKw(info?.capacity || 3); setBookingOpen(true); }} />
                    </div>
                </div>
            </section>

            {/* Trust Badge Row — slim marquee of proof points right under the hero */}
            <TrustBadgeRow t={t} />

            {/* Solar Types */}
            <section id='solar-types' className='max-w-7xl mx-auto px-4 sm:px-8 py-6 sm:py-14 scroll-mt-20'>
                <div className='text-center'>
                    <Badge variant='outline'>Solar Systems</Badge>
                    <h2 className='font-display text-2xl sm:text-4xl font-semibold mt-2'>{t('nav_solar_types')}</h2>
                    <p className='text-xs sm:text-sm text-muted-foreground mt-1 max-w-2xl mx-auto'>Choose the right system for your needs — we help you decide based on your bill, roof and outage frequency.</p>
                </div>
                <div className='grid md:grid-cols-3 gap-3 sm:gap-4 mt-4 sm:mt-8'>
                    {(() => {
                        // CMS-driven when admin has configured cards; otherwise fall back to
                        // the classic On-Grid / Off-Grid / Hybrid triad with translation keys.
                        const cmsCards = cms?.solar_types;
                        if (Array.isArray(cmsCards) && cmsCards.length > 0) {
                            const iconMap = { on_grid: Zap, off_grid: Battery, hybrid: Sun };
                            const tintMap = {
                                on_grid: 'text-emerald-700 bg-gradient-to-br from-emerald-50 to-emerald-100 ring-1 ring-emerald-200',
                                off_grid: 'text-blue-700 bg-gradient-to-br from-blue-50 to-blue-100 ring-1 ring-blue-200',
                                hybrid: 'text-accent bg-gradient-to-br from-amber-50 to-orange-100 ring-1 ring-orange-200',
                            };
                            return cmsCards.map((card, idx) => {
                                const Icon = iconMap[card.key] || Sun;
                                const tint = tintMap[card.key] || 'text-slate-700 bg-gradient-to-br from-slate-50 to-slate-100 ring-1 ring-slate-200';
                                const title = card.title?.[lang] || card.title?.en || '';
                                const desc = card.body?.[lang] || card.body?.en || '';
                                const tag = card.tag?.[lang] || card.tag?.en || '';
                                return (
                                    <Card key={card.key || idx} className='card-elevate hover:border-accent/40 hover:shadow-lg transition group' data-testid={`solar-type-${card.key || idx}`}>
                                        <CardContent className='p-4 sm:p-6'>
                                            <div className='flex items-start justify-between'>
                                                <div className={`w-11 h-11 sm:w-14 sm:h-14 rounded-2xl grid place-items-center ${tint} group-hover:scale-105 transition-transform text-2xl`}>
                                                    {card.emoji ? <span>{card.emoji}</span> : <Icon className='w-5 h-5 sm:w-7 sm:h-7' />}
                                                </div>
                                                {tag && <Badge variant='outline' className='text-[10px]'>{tag}</Badge>}
                                            </div>
                                            <div className='font-display text-base sm:text-xl font-semibold mt-2 sm:mt-3'>{title}</div>
                                            <div className='text-xs sm:text-sm text-muted-foreground mt-1 sm:mt-2'>{desc}</div>
                                        </CardContent>
                                    </Card>
                                );
                            });
                        }
                        return [
                            { icon: Zap, name: t('ongrid_title'), desc: t('ongrid_desc'), color: 'text-emerald-700 bg-gradient-to-br from-emerald-50 to-emerald-100 ring-1 ring-emerald-200', badge: 'Most Popular' },
                            { icon: Battery, name: t('offgrid_title'), desc: t('offgrid_desc'), color: 'text-blue-700 bg-gradient-to-br from-blue-50 to-blue-100 ring-1 ring-blue-200', badge: 'Power Cuts?' },
                            { icon: Sun, name: t('hybrid_title'), desc: t('hybrid_desc'), color: 'text-accent bg-gradient-to-br from-amber-50 to-orange-100 ring-1 ring-orange-200', badge: 'Best of Both' },
                        ].map(s => (
                            <Card key={s.name} className='card-elevate hover:border-accent/40 hover:shadow-lg transition group'>
                                <CardContent className='p-4 sm:p-6'>
                                    <div className='flex items-start justify-between'>
                                        <div className={`w-11 h-11 sm:w-14 sm:h-14 rounded-2xl grid place-items-center ${s.color} group-hover:scale-105 transition-transform`}>
                                            <s.icon className='w-5 h-5 sm:w-7 sm:h-7' />
                                        </div>
                                        <Badge variant='outline' className='text-[10px]'>{s.badge}</Badge>
                                    </div>
                                    <div className='font-display text-base sm:text-xl font-semibold mt-2 sm:mt-3'>{s.name}</div>
                                    <div className='text-xs sm:text-sm text-muted-foreground mt-1 sm:mt-2'>{s.desc}</div>
                                </CardContent>
                            </Card>
                        ));
                    })()}
                </div>
            </section>

            {/* Solar Solutions — Segmented services (subpages) */}
            <section id='solutions' className='relative py-16 scroll-mt-20 bg-gradient-to-br from-sky-50 via-blue-50/60 to-white dark:from-sky-950/30 dark:via-blue-950/20 dark:to-background'>
                <div className='max-w-7xl mx-auto px-4 sm:px-8'>
                    <div className='text-center'>
                        <Badge className='bg-sky-100 text-sky-700 border-sky-200 hover:bg-sky-100'>Solar Solutions</Badge>
                        <h2 className='font-display text-3xl sm:text-4xl font-semibold mt-2'>Solutions For Every Need</h2>
                        <p className='text-sm text-muted-foreground mt-1 max-w-2xl mx-auto'>
                            From rooftops to factories and even atta chakkis — we design the right solar setup for your usage pattern.
                        </p>
                    </div>
                    <div className='grid sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-10'>
                        {SOLUTIONS.map(s => (
                            <button
                                key={s.key}
                                onClick={() => setSolutionOpen(s.key)}
                                className='text-left group'
                                data-testid={`solution-card-${s.key}`}
                            >
                                <Card className={`card-elevate hover:border-sky-400/60 hover:shadow-xl transition h-full bg-gradient-to-b ${s.cardTint} border`}>
                                    <CardContent className='p-6'>
                                        <div className={`w-16 h-16 rounded-2xl grid place-items-center ${s.iconBg} group-hover:scale-110 transition-transform`}>
                                            <s.icon className='w-8 h-8' strokeWidth={2} />
                                        </div>
                                        <div className='font-display text-xl font-semibold mt-4 group-hover:text-primary transition flex items-center gap-2'>
                                            {s.title}
                                            <span className='text-lg opacity-80'>{s.emoji}</span>
                                        </div>
                                        <div className='text-xs text-muted-foreground mt-0.5'>{s.subtitle}</div>
                                        <div className='text-sm text-muted-foreground mt-3 line-clamp-3'>{s.short}</div>
                                        <div className='mt-4 inline-flex items-center gap-1 text-sm text-primary font-medium'>
                                            Learn more <ArrowRight className='w-3.5 h-3.5 group-hover:translate-x-1 transition-transform' />
                                        </div>
                                    </CardContent>
                                </Card>
                            </button>
                        ))}
                    </div>
                </div>
            </section>

            {/* Our Offering */}
            <section id='offering' className='max-w-7xl mx-auto px-4 sm:px-8 py-14 scroll-mt-20'>
                <div className='text-center'>
                    <Badge variant='outline'>Services</Badge>
                    <h2 className='font-display text-3xl sm:text-4xl font-semibold mt-2'>{t('nav_offering')}</h2>
                    <p className='text-sm text-muted-foreground mt-1'>End-to-end solar solutions — designed, installed and maintained by UPPOWER SOLAR.</p>
                </div>
                <div className='grid grid-cols-2 lg:grid-cols-4 gap-4 mt-8'>
                    {[
                        { icon: IndianRupee, title: t('landing_guaranteed_subsidy'), desc: t('landing_guaranteed_subsidy_desc') },
                        { icon: Wrench, title: t('landing_hassle_free'), desc: t('landing_hassle_free_desc') },
                        { icon: Wind, title: t('landing_storm_proof'), desc: t('landing_storm_proof_desc') },
                        { icon: Shield, title: t('landing_amc'), desc: t('landing_amc_desc') },
                    ].map(w => (
                        <div key={w.title} className='p-5 rounded-2xl border bg-card card-elevate'>
                            <div className='w-11 h-11 rounded-xl bg-accent/10 text-accent grid place-items-center'><w.icon className='w-5 h-5' /></div>
                            <div className='font-semibold mt-3'>{w.title}</div>
                            <div className='text-sm text-muted-foreground mt-1'>{w.desc}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Rate chart — data comes from admin-editable subsidy_rates so admins can
                 tune system cost, subsidy amounts, and booking amount without a deploy. */}
            <section className='max-w-7xl mx-auto px-4 sm:px-8 py-14'>
                <h2 className='font-display text-3xl font-semibold text-center'>{t('landing_rate_chart')}</h2>
                <div className='text-center text-sm text-muted-foreground mt-1'>{t('landing_rate_sub')}</div>
                <div className='max-w-5xl mx-auto mt-6 overflow-hidden rounded-2xl border bg-card card-elevate'>
                    <table className='w-full text-sm'>
                        <thead className='bg-primary text-primary-foreground'>
                            <tr>
                                <th className='px-3 py-3 text-left'>{t('col_capacity')}</th>
                                <th className='px-3 py-3 text-right'>{t('col_system_cost')}</th>
                                <th className='px-3 py-3 text-right'>{t('col_subsidy')}</th>
                                <th className='px-3 py-3 text-right'>{t('col_your_cost')}</th>
                                <th className='px-3 py-3 text-right'>Book Now</th>
                            </tr>
                        </thead>
                        <tbody>
                            {(rateChart.length ? rateChart : [
                                { kw: 1, system_cost: 65000, central_subsidy: 30000, state_subsidy: 15000, booking_amount: 1000 },
                                { kw: 2, system_cost: 130000, central_subsidy: 60000, state_subsidy: 30000, booking_amount: 1500 },
                                { kw: 3, system_cost: 180000, central_subsidy: 78000, state_subsidy: 30000, booking_amount: 2000 },
                                { kw: 4, system_cost: 240000, central_subsidy: 78000, state_subsidy: 30000, booking_amount: 2500 },
                                { kw: 5, system_cost: 300000, central_subsidy: 78000, state_subsidy: 30000, booking_amount: 3000 },
                            ]).map((r) => {
                                const sys = Number(r.system_cost || 0);
                                const sub = Number(r.central_subsidy || 0) + Number(r.state_subsidy || 0);
                                const you = Math.max(0, sys - sub);
                                const highlight = Number(r.kw) === 3;   // pin 3 kW as the recommended
                                const bookingAmt = Number(r.booking_amount || 0);
                                const fmt = (n) => Number(n).toLocaleString('en-IN');
                                return (
                                    <tr key={r.id || r.kw} className={`border-t ${highlight ? 'bg-accent/10' : ''}`} data-testid={`rate-row-${r.kw}kw`}>
                                        <td className='px-3 py-3 font-medium whitespace-nowrap'>{r.kw} KW{highlight ? ' ⭐' : ''}</td>
                                        <td className='px-3 py-3 text-right tabular-nums'>₹{fmt(sys)}</td>
                                        <td className='px-3 py-3 text-right font-semibold text-accent tabular-nums'>₹{fmt(sub)}</td>
                                        <td className='px-3 py-3 text-right font-semibold text-primary tabular-nums'>₹{fmt(you)}</td>
                                        <td className='px-3 py-3 text-right whitespace-nowrap'>
                                            {bookingAmt > 0 ? (
                                                <Button
                                                    size='sm'
                                                    className='h-8'
                                                    onClick={() => { setBookingKw(Number(r.kw)); setBookingOpen(true); }}
                                                    data-testid={`rate-book-${r.kw}kw`}
                                                >
                                                    ₹{fmt(bookingAmt)}
                                                </Button>
                                            ) : (
                                                <span className='text-xs text-muted-foreground'>—</span>
                                            )}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                </div>
                <div className='text-center text-[11px] text-muted-foreground mt-3'>
                    Booking amount भरकर पेमेंट करें — Razorpay से secure payment · GST invoice · fully refundable if cancelled within 24 hrs.
                </div>
            </section>

            {/* Journey */}
            <section className='max-w-7xl mx-auto px-4 sm:px-8 py-14'>
                <h2 className='font-display text-3xl font-semibold text-center'>{t('landing_journey')}</h2>
                <div className='grid md:grid-cols-4 gap-4 mt-8'>
                    {[['1', t('step_1'), t('step_1_desc')],['2', t('step_2'), t('step_2_desc')],['3', t('step_3'), t('step_3_desc')],['4', t('step_4'), t('step_4_desc')]].map(([n, ti, d]) => (
                        <div key={n} className='p-5 rounded-2xl border bg-card'>
                            <div className='w-9 h-9 rounded-full bg-primary text-primary-foreground grid place-items-center font-semibold'>{n}</div>
                            <div className='font-semibold mt-3'>{ti}</div>
                            <div className='text-sm text-muted-foreground mt-1'>{d}</div>
                        </div>
                    ))}
                </div>
            </section>

            {/* Solar Saathi — Trusted Partner Section */}
            <section id='solar-saathi' className='relative py-16 bg-gradient-to-br from-amber-50 via-orange-50/50 to-yellow-50 dark:from-amber-950/20 dark:via-orange-950/10 dark:to-yellow-950/20 scroll-mt-20 overflow-hidden'>
                <div className='absolute inset-0 opacity-[0.03] pointer-events-none' style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, currentColor 1px, transparent 0)', backgroundSize: '24px 24px' }} />
                <div className='max-w-7xl mx-auto px-4 sm:px-8 relative'>
                    <div className='grid lg:grid-cols-2 gap-10 items-center'>
                        <div>
                            <Badge className='bg-amber-100 text-amber-800 border-amber-200 hover:bg-amber-100' data-testid='saathi-kicker'>
                                <HeartHandshake className='w-3.5 h-3.5 mr-1' />
                                {t('saathi_kicker')}
                            </Badge>
                            <h2 className='font-display text-3xl sm:text-4xl font-semibold mt-3 leading-tight'>
                                {t('saathi_title_1')}<br />
                                <span className='text-amber-600 dark:text-amber-400'>{t('saathi_title_2')}</span>
                            </h2>
                            <p className='mt-4 text-muted-foreground leading-relaxed'>{t('saathi_body')}</p>
                            <div className='mt-6 space-y-3'>
                                {[t('saathi_b1'), t('saathi_b2'), t('saathi_b3')].map((b) => (
                                    <div key={b} className='flex items-start gap-3'>
                                        <div className='w-6 h-6 rounded-full bg-amber-500 text-white grid place-items-center shrink-0 mt-0.5'>
                                            <CheckCircle2 className='w-4 h-4' />
                                        </div>
                                        <span className='text-sm font-medium'>{b}</span>
                                    </div>
                                ))}
                            </div>
                            <div className='mt-7 flex gap-2 flex-wrap'>
                                <Button size='lg' className='bg-amber-600 hover:bg-amber-700 text-white min-h-[48px]' onClick={() => setSaathiOpen(true)} data-testid='saathi-book-visit'>
                                    <HomeIcon className='w-4 h-4 mr-2' />
                                    {t('saathi_book_cta') || 'Book Solar Saathi Visit'}
                                </Button>
                                <Button size='lg' variant='outline' className='min-h-[48px]' onClick={() => setQuoteOpen(true)} data-testid='saathi-cta'>
                                    <Phone className='w-4 h-4 mr-2' />
                                    {t('get_free_quote')}
                                </Button>
                            </div>
                        </div>
                        <div className='grid grid-cols-2 gap-4'>
                            {[
                                { icon: FileText, title: t('saathi_paperwork'), desc: t('saathi_paperwork_d'), tint: 'from-sky-400 to-blue-600', bg: 'bg-sky-50 dark:bg-sky-950/30' },
                                { icon: Wrench, title: t('saathi_maint'), desc: t('saathi_maint_d'), tint: 'from-emerald-400 to-green-600', bg: 'bg-emerald-50 dark:bg-emerald-950/30' },
                                { icon: Landmark, title: t('saathi_finance'), desc: t('saathi_finance_d'), tint: 'from-purple-400 to-indigo-600', bg: 'bg-purple-50 dark:bg-purple-950/30' },
                                { icon: HardHat, title: t('saathi_install'), desc: t('saathi_install_d'), tint: 'from-amber-400 to-orange-600', bg: 'bg-amber-50 dark:bg-amber-950/30' },
                            ].map((c, i) => (
                                <div key={c.title} className={`p-5 rounded-2xl border bg-card card-elevate transition-transform hover:-translate-y-1 duration-200`} data-testid={`saathi-pillar-${i}`}>
                                    <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${c.tint} text-white grid place-items-center shadow-lg`}>
                                        <c.icon className='w-6 h-6' />
                                    </div>
                                    <div className='font-semibold mt-3'>{c.title}</div>
                                    <div className='text-xs text-muted-foreground mt-1 leading-relaxed'>{c.desc}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* About */}
            <section id='about' className='max-w-7xl mx-auto px-4 sm:px-8 py-14 scroll-mt-20'>
                <div className='grid lg:grid-cols-2 gap-8 items-center'>
                    <div>
                        <Badge variant='outline'>About Us</Badge>
                        <h2 className='font-display text-3xl sm:text-4xl font-semibold mt-2'>{cmsText('about.title', 'nav_about')}</h2>
                        <p className='mt-4 text-muted-foreground whitespace-pre-line'>
                            {cmsText('about.body') || `UPPOWER SOLAR (यूपी पावर एनर्जी) — Uttar Pradesh's trusted rooftop solar partner.
We help homes and businesses switch to clean energy under the PM Surya Ghar Muft Bijli Yojana with zero-hassle installation, guaranteed subsidy processing, and 5-year AMC.`}
                        </p>
                        <div className='mt-4 grid grid-cols-3 gap-3'>
                            <MiniStat label='Happy Homes' value='1000+' icon={HomeIcon} />
                            <MiniStat label='Awards' value='Nat’l Vendor' icon={Award} />
                            <MiniStat label='Support' value='24×7' icon={HeartHandshake} />
                        </div>
                    </div>
                    {cms?.about?.image_url ? (
                        <div className='rounded-2xl overflow-hidden shadow-lg ring-1 ring-black/5'>
                            <img src={cms.about.image_url} alt='About UPPOWER Solar' className='w-full h-full object-cover max-h-[380px]' data-testid='landing-about-image' />
                        </div>
                    ) : (
                        <Card className='card-elevate'>
                            <CardContent className='p-6'>
                                <div className='font-display text-lg font-semibold'>Our Mission</div>
                                <p className='mt-2 text-sm text-muted-foreground'>
                                    To make solar energy accessible to every home in UP — with transparent pricing, honest advice
                                    and reliable service. Aapki bachat, humari zimmedari.
                                </p>
                                <div className='mt-4 space-y-2 text-sm'>
                                    {['Authorized National Vendor', 'End-to-end paperwork', 'Bank loan tie-ups (0 EMI)', '5-year free AMC'].map(k => (
                                        <div key={k} className='flex items-center gap-2'><CheckCircle2 className='w-4 h-4 text-accent' /> {k}</div>
                                    ))}
                                </div>
                            </CardContent>
                        </Card>
                    )}
                </div>
            </section>

            {/* Interactive Savings Slider */}
            <section id='savings' className='py-12 sm:py-14 bg-gradient-to-br from-emerald-50 via-white to-sky-50 dark:from-emerald-950/20 dark:to-sky-950/20'>
                <div className='max-w-5xl mx-auto px-4 sm:px-8'>
                    <div className='text-center mb-6'>
                        <Badge className='bg-emerald-100 text-emerald-700 border-emerald-200'>Instant Estimator</Badge>
                        <h2 className='font-display text-3xl sm:text-4xl font-semibold mt-2'>See Your Savings — Instantly</h2>
                        <p className='text-sm text-muted-foreground mt-1'>Drag the slider to your rooftop size — no signup, no form.</p>
                    </div>
                    <SavingsSlider onQuote={openQuoteWithKw} />
                </div>
            </section>

            {/* Bank Loan Partners */}
            <section id='bank-partners' className='max-w-7xl mx-auto px-4 sm:px-8 py-14 scroll-mt-20'>
                <div className='text-center'>
                    <Badge className='bg-indigo-100 text-indigo-700 border-indigo-200'>
                        <Landmark className='w-3.5 h-3.5 mr-1' /> {t('bank_kicker') || 'Easy Financing'}
                    </Badge>
                    <h2 className='font-display text-3xl sm:text-4xl font-semibold mt-2'>{t('bank_title') || 'Solar Loans From Trusted Banks'}</h2>
                    <p className='text-sm text-muted-foreground mt-1 max-w-2xl mx-auto'>
                        {t('bank_sub') || 'Zero-down solar with easy EMIs. Pre-approved partner tie-ups — apply in minutes.'}
                    </p>
                </div>
                <div className='grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3 mt-8'>
                    {[
                        { name: 'SBI', full: 'State Bank of India', href: 'https://sbi.co.in/web/personal-banking/loans/home-loans/solar-loans-suryashakti' },
                        { name: 'HDFC', full: 'HDFC Bank', href: 'https://www.hdfcbank.com/personal/borrow/popular-loans/personal-loan' },
                        { name: 'ICICI', full: 'ICICI Bank', href: 'https://www.icicibank.com/personal-banking/loans' },
                        { name: 'PNB', full: 'Punjab National Bank', href: 'https://www.pnbindia.in/pnb-surya-shakti.html' },
                        { name: 'BoB', full: 'Bank of Baroda', href: 'https://www.bankofbaroda.in/personal-banking/loans' },
                        { name: 'Canara', full: 'Canara Bank', href: 'https://canarabank.com/pages/loans' },
                    ].map((b, i) => (
                        <a key={b.name}
                           href={b.href}
                           target='_blank' rel='noreferrer'
                           className='group relative rounded-2xl border bg-card p-4 card-elevate hover:-translate-y-1 hover:shadow-lg transition-all duration-200 flex flex-col items-center text-center'
                           data-testid={`bank-partner-${i}`}>
                            <div className='w-20 h-16 rounded-xl bg-white ring-1 ring-slate-200 dark:ring-slate-700 grid place-items-center overflow-hidden p-2 shadow-sm'>
                                <BankLogo name={b.name} />
                            </div>
                            <div className='font-semibold text-xs mt-2.5 leading-tight'>{b.full}</div>
                            <div className='text-[10px] text-muted-foreground mt-0.5'>from <span className='font-semibold text-emerald-600 dark:text-emerald-400'>5.75%</span> p.a.</div>
                            <div className='mt-2 text-[10px] font-semibold text-primary inline-flex items-center gap-0.5 opacity-70 group-hover:opacity-100'>
                                Apply <ArrowRight className='w-3 h-3' />
                            </div>
                        </a>
                    ))}
                </div>
                <div className='mt-8 grid sm:grid-cols-3 gap-3 max-w-3xl mx-auto text-sm'>
                    <div className='flex items-center gap-2 p-3 rounded-lg bg-emerald-50 dark:bg-emerald-950/30 border border-emerald-100 dark:border-emerald-900/50'>
                        <CheckCircle2 className='w-4 h-4 text-emerald-600 shrink-0' />
                        <span>{t('bank_perk_1') || '0% down payment options'}</span>
                    </div>
                    <div className='flex items-center gap-2 p-3 rounded-lg bg-sky-50 dark:bg-sky-950/30 border border-sky-100 dark:border-sky-900/50'>
                        <CheckCircle2 className='w-4 h-4 text-sky-600 shrink-0' />
                        <span>{t('bank_perk_2') || 'Tenure up to 10 years'}</span>
                    </div>
                    <div className='flex items-center gap-2 p-3 rounded-lg bg-amber-50 dark:bg-amber-950/30 border border-amber-100 dark:border-amber-900/50'>
                        <CheckCircle2 className='w-4 h-4 text-amber-600 shrink-0' />
                        <span>{t('bank_perk_3') || 'Full documentation help'}</span>
                    </div>
                </div>
                <div className='text-center mt-6'>
                    <Button variant='outline' size='lg' onClick={() => setQuoteOpen(true)} data-testid='bank-cta' className='min-h-[48px]'>
                        <IndianRupee className='w-4 h-4 mr-1' /> {t('bank_cta') || 'Check My EMI Eligibility'}
                    </Button>
                </div>
            </section>

            {/* Download Android App — QR + big CTA */}
            <DownloadAppSection />

            {/* Success Stories — CMS testimonials preferred; falls back to legacy StoryGrid */}
            <section id='stories' className='max-w-7xl mx-auto px-4 sm:px-8 py-14'>
                <div className='text-center mb-8'>
                    <Badge variant='outline'>Happy Customers</Badge>
                    <h2 className='font-display text-3xl sm:text-4xl font-semibold mt-2'>Real Homes, Real Savings</h2>
                    <p className='text-sm text-muted-foreground mt-1'>Families across UP are cutting their bills to almost zero.</p>
                </div>
                {Array.isArray(cms?.testimonials) && cms.testimonials.length > 0 ? (
                    <TestimonialGrid items={cms.testimonials} lang={lang} />
                ) : (
                    <StoryGrid stories={stories} />
                )}
                <div className='text-center mt-8'>
                    <Button size='lg' className='bg-accent hover:bg-accent/90 text-accent-foreground min-h-[52px]' onClick={() => setQuoteOpen(true)} data-testid='stories-cta'>
                        Join 5000+ Happy Customers <ArrowRight className='ml-2 w-4 h-4' />
                    </Button>
                </div>
            </section>

            {/* FAQ — CMS-driven with legacy translation fallback */}
            <section id='faq' className='max-w-4xl mx-auto px-4 sm:px-8 py-14 scroll-mt-20'>
                <h2 className='font-display text-3xl font-semibold text-center'>{t('landing_faq')}</h2>
                <div className='mt-6 space-y-3'>
                    {(() => {
                        const cmsFaqs = cms?.faqs;
                        const items = (Array.isArray(cmsFaqs) && cmsFaqs.length > 0)
                            ? cmsFaqs.map((f, i) => ({
                                q: f.q?.[lang] || f.q?.en || '',
                                a: f.a?.[lang] || f.a?.en || '',
                                k: f.id || `cms-${i}`,
                            }))
                            : [
                                [t('faq_1_q'), t('faq_1_a')],
                                [t('faq_2_q'), t('faq_2_a')],
                                [t('faq_3_q'), t('faq_3_a')],
                                [t('faq_4_q'), t('faq_4_a')],
                                [t('faq_5_q'), t('faq_5_a')],
                            ].map(([q, a], i) => ({ q, a, k: `t-${i}` }));
                        return items.filter(x => x.q).map(x => (
                            <details key={x.k} className='rounded-xl border bg-card p-4 group' data-testid={`faq-${x.k}`}>
                                <summary className='font-medium cursor-pointer flex items-center justify-between'>{x.q} <ChevronDown className='w-4 h-4 text-muted-foreground group-open:rotate-180 transition-transform' /></summary>
                                <div className='text-sm text-muted-foreground mt-2 whitespace-pre-line'>{x.a}</div>
                            </details>
                        ));
                    })()}
                </div>
            </section>

            {/* Careers */}
            <section id='careers' className='max-w-7xl mx-auto px-4 sm:px-8 py-14 scroll-mt-20'>
                <div className='text-center'>
                    <Badge variant='outline'>Careers</Badge>
                    <h2 className='font-display text-3xl sm:text-4xl font-semibold mt-2'>{t('nav_careers')}</h2>
                    <p className='text-sm text-muted-foreground mt-1'>Join us in powering UP with clean energy.</p>
                </div>
                <div className='grid md:grid-cols-2 gap-3 mt-8'>
                    {careers.map(c => (
                        <Card key={c.id} className='hover:border-accent/40'>
                            <CardContent className='p-5 flex items-start gap-3'>
                                <div className='w-10 h-10 rounded-xl bg-accent/10 text-accent grid place-items-center'><Briefcase className='w-5 h-5' /></div>
                                <div className='flex-1'>
                                    <div className='font-semibold'>{c.title}</div>
                                    <div className='text-xs text-muted-foreground'>{c.location} • {c.type}</div>
                                    <div className='text-sm mt-1'>{c.summary}</div>
                                    <Button size='sm' variant='outline' className='mt-2' onClick={() => setCareerApply(c)} data-testid={`career-apply-${c.id}`}>Apply</Button>
                                </div>
                            </CardContent>
                        </Card>
                    ))}
                    {careers.length === 0 && <div className='text-sm text-muted-foreground'>Openings will appear here.</div>}
                </div>
            </section>

            <footer className='mt-12 bg-gradient-to-br from-primary via-primary to-blue-900 text-primary-foreground'>
                <div className='max-w-7xl mx-auto px-4 sm:px-8 py-10'>
                    <div className='grid grid-cols-2 md:grid-cols-4 gap-8'>
                        <div className='col-span-2 md:col-span-1'>
                            <div className='flex items-center gap-2'>
                                <div className='w-10 h-10 rounded-xl bg-white/10 grid place-items-center'>
                                    <Sun className='w-6 h-6 text-accent' />
                                </div>
                                <div>
                                    <div className='font-display font-semibold text-lg'>UPPOWER SOLAR™</div>
                                    <div className='text-xs opacity-70'>यूपी पावर एनर्जी</div>
                                </div>
                            </div>
                            <p className='text-xs opacity-80 mt-3 leading-relaxed'>
                                PM Surya Ghar & Muft Bijli Yojana ke channel partner. Rooftop solar for every home & business — with subsidy, EMI and 25-year warranty.
                            </p>
                        </div>

                        <div>
                            <div className='font-semibold text-sm uppercase tracking-wide mb-3 text-accent'>Explore</div>
                            <ul className='space-y-2 text-sm'>
                                <li><a href='#home' className='opacity-90 hover:opacity-100 hover:text-accent'>Home</a></li>
                                <li><a href='#solar-types' className='opacity-90 hover:opacity-100 hover:text-accent'>Solar Types</a></li>
                                <li><a href='#solutions' className='opacity-90 hover:opacity-100 hover:text-accent'>Solutions</a></li>
                                <li><a href='#offering' className='opacity-90 hover:opacity-100 hover:text-accent'>Our Offering</a></li>
                                <li><a href='#about' className='opacity-90 hover:opacity-100 hover:text-accent'>About Us</a></li>
                                <li><a href='#careers' className='opacity-90 hover:opacity-100 hover:text-accent'>Careers</a></li>
                                <li><a href='#faq' className='opacity-90 hover:opacity-100 hover:text-accent'>FAQ</a></li>
                            </ul>
                        </div>

                        <div>
                            <div className='font-semibold text-sm uppercase tracking-wide mb-3 text-accent'>Reach Us</div>
                            <div className='space-y-3 text-sm'>
                                <div className='flex items-start gap-2'>
                                    <Phone className='w-4 h-4 text-accent shrink-0 mt-0.5' />
                                    <div className='flex-1'>
                                        <div className='text-[10px] uppercase opacity-70'>Helpline</div>
                                        {cmsPlain('contact.phone') ? (
                                            <a href={`tel:${cmsPlain('contact.phone').replace(/[^0-9+]/g, '')}`} className='block font-bold text-base hover:text-accent tabular-nums' data-testid='footer-phone'>{cmsPlain('contact.phone')}</a>
                                        ) : (
                                            <>
                                                <a href='tel:01169266215' className='block font-bold text-base hover:text-accent tabular-nums'>011-69266215</a>
                                                <a href='tel:9235373737' className='block font-bold text-base hover:text-accent tabular-nums'>+91 9235373737</a>
                                            </>
                                        )}
                                        {/* Secondary landline / alt number — same visual weight
                                            as the primary helpline so both feel equally reachable. */}
                                        {cmsPlain('contact.phone2') && (
                                            <a
                                                href={`tel:${cmsPlain('contact.phone2').replace(/[^0-9+]/g, '')}`}
                                                className='block font-bold text-base hover:text-accent tabular-nums'
                                                data-testid='footer-phone2'
                                            >
                                                {cmsPlain('contact.phone2')}
                                            </a>
                                        )}
                                    </div>
                                </div>
                                <div className='flex items-start gap-2'>
                                    <Mail className='w-4 h-4 text-accent shrink-0 mt-0.5' />
                                    <div>
                                        <div className='text-[10px] uppercase opacity-70'>Email</div>
                                        <a href={`mailto:${cmsPlain('contact.email', 'support@upsolar.in')}`} className='font-bold hover:text-accent break-all' data-testid='footer-email'>{cmsPlain('contact.email', 'support@upsolar.in')}</a>
                                    </div>
                                </div>
                                {(cmsPlain('contact.hours') || cmsText('contact.hours')) && (
                                    <div className='text-[11px] opacity-80 flex items-start gap-1'>
                                        <span className='opacity-70'>🕒</span>
                                        <span>{cmsText('contact.hours')}</span>
                                    </div>
                                )}
                                <a href={`https://wa.me/${(cmsPlain('contact.whatsapp') || '919235373737').replace(/[^0-9]/g, '')}`} target='_blank' rel='noreferrer' className='inline-flex items-center gap-1 text-xs bg-[#25D366] hover:bg-[#1fbd58] text-white px-3 py-2 rounded-lg font-semibold' data-testid='footer-whatsapp'>
                                    💬 Chat on WhatsApp
                                </a>
                            </div>
                        </div>

                        <div>
                            <div className='font-semibold text-sm uppercase tracking-wide mb-3 text-accent'>Our Offices</div>
                            <div className='text-sm font-semibold mb-3 text-white'>ZeroKart Technologies Private Limited</div>
                            <div className='space-y-3 text-sm'>
                                <div>
                                    <div className='flex items-center gap-1 font-semibold text-accent'><MapPin className='w-3.5 h-3.5' /> Corporate Office</div>
                                    <div className='text-xs opacity-90 leading-relaxed mt-1'>
                                        20A/2, Second Floor, Singh Building, Opposite 11 No. Crossing, Gurudev Chauraha, <b>Kanpur, UP - 208024</b>
                                    </div>
                                </div>
                                <div>
                                    <div className='flex items-center gap-1 font-semibold text-accent'><MapPin className='w-3.5 h-3.5' /> Registered Office</div>
                                    <div className='text-xs opacity-90 leading-relaxed mt-1'>
                                        105A First Floor, Tambeshwar Chauraha, Jail Road, <b>Fatehpur, UP - 212601</b>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className='mt-10 pt-6 border-t border-white/10 flex flex-col sm:flex-row items-center justify-between gap-3 text-xs opacity-80'>
                        <div>© {new Date().getFullYear()} UPPOWER SOLAR™ • यूपी पावर एनर्जी • All rights reserved.</div>
                        <div className='flex items-center gap-4 font-medium'>
                            <a href='/privacy' className='hover:underline'>Privacy Policy</a>
                            <span>•</span>
                            <span>Made with <span className='text-red-500 text-base animate-pulse'>❤️</span> in India</span>
                        </div>
                        <div>www.uppower.in • support@upsolar.in</div>
                    </div>
                </div>
            </footer>

            <QuoteDialog open={quoteOpen} onClose={() => { setQuoteOpen(false); setQuoteKw(null); }} t={t} initialKw={quoteKw} />
            <BookingDialog open={bookingOpen} onClose={() => setBookingOpen(false)} capacity={bookingKw} />
            <SaathiBookingDialog open={saathiOpen} onClose={() => setSaathiOpen(false)} t={t} />
            <CareerApplyDialog open={!!careerApply} onOpenChange={(o) => { if (!o) setCareerApply(null); }} career={careerApply} />

            {/* Sticky Mobile Get Quote Bar */}
            <div className='md:hidden fixed bottom-0 left-0 right-0 z-50 bg-card/95 backdrop-blur border-t shadow-lg px-3 py-2.5' data-testid='mobile-sticky-cta'>
                <div className='flex items-center gap-2'>
                    <a href='tel:9235373737' className='shrink-0'>
                        <Button variant='outline' size='lg' className='min-h-[48px] px-3'>
                            <Phone className='w-4 h-4' />
                        </Button>
                    </a>
                    <a href='https://wa.me/919235373737' target='_blank' rel='noreferrer' className='shrink-0'>
                        <Button size='lg' className='min-h-[48px] px-3 bg-[#25D366] hover:bg-[#1fbd58] text-white'>
                            💬
                        </Button>
                    </a>
                    <Button size='lg' className='flex-1 min-h-[48px] bg-accent hover:bg-accent/90 text-accent-foreground font-semibold' onClick={() => setQuoteOpen(true)} data-testid='mobile-sticky-quote'>
                        {t('get_free_quote')} <ArrowRight className='ml-2 w-4 h-4' />
                    </Button>
                </div>
            </div>

            {/* Solution Detail Dialog */}
            <Dialog open={!!solutionOpen} onOpenChange={(o) => !o && setSolutionOpen(null)}>
                <DialogContent className='max-w-2xl max-h-[90vh] overflow-y-auto'>
                    {activeSolution && (
                        <>
                            <DialogHeader>
                                <div className='flex items-center gap-3'>
                                    <div className={`w-14 h-14 rounded-2xl grid place-items-center ${activeSolution.iconBg}`}>
                                        <activeSolution.icon className='w-7 h-7' />
                                    </div>
                                    <div>
                                        <DialogTitle className='text-2xl'>{activeSolution.title}</DialogTitle>
                                        <div className='text-sm text-muted-foreground'>{activeSolution.subtitle}</div>
                                    </div>
                                </div>
                            </DialogHeader>
                            <div className='space-y-4'>
                                <p className='text-sm leading-relaxed'>{activeSolution.short}</p>
                                <div className='grid sm:grid-cols-3 gap-2'>
                                    <div className='rounded-lg border p-3 bg-sky-50 dark:bg-sky-950/30'>
                                        <div className='text-[10px] uppercase text-muted-foreground'>Typical Savings</div>
                                        <div className='font-semibold text-primary mt-0.5'>{activeSolution.savings}</div>
                                    </div>
                                    <div className='rounded-lg border p-3 bg-emerald-50 dark:bg-emerald-950/30'>
                                        <div className='text-[10px] uppercase text-muted-foreground'>Payback Period</div>
                                        <div className='font-semibold text-primary mt-0.5'>{activeSolution.payback}</div>
                                    </div>
                                    <div className='rounded-lg border p-3 bg-amber-50 dark:bg-amber-950/30'>
                                        <div className='text-[10px] uppercase text-muted-foreground'>Ideal For</div>
                                        <div className='text-xs font-medium mt-0.5'>{activeSolution.idealFor}</div>
                                    </div>
                                </div>
                                <div>
                                    <div className='font-semibold text-sm mb-2'>What's Included</div>
                                    <ul className='space-y-1.5'>
                                        {activeSolution.highlights.map(h => (
                                            <li key={h} className='flex items-start gap-2 text-sm'>
                                                <CheckCircle2 className='w-4 h-4 text-primary shrink-0 mt-0.5' />
                                                <span>{h}</span>
                                            </li>
                                        ))}
                                    </ul>
                                </div>
                                <div className='flex flex-col sm:flex-row gap-2 pt-2'>
                                    <Button className='flex-1 bg-accent hover:bg-accent/90 text-accent-foreground' onClick={() => { setSolutionOpen(null); setQuoteOpen(true); }} data-testid={`solution-quote-${activeSolution.key}`}>
                                        Get a Custom Quote <ArrowRight className='ml-1 w-4 h-4' />
                                    </Button>
                                    <a href='tel:9235373737' className='flex-1'>
                                        <Button variant='outline' className='w-full'>
                                            <Phone className='w-4 h-4 mr-1' /> Talk to expert
                                        </Button>
                                    </a>
                                </div>
                            </div>
                        </>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}

function MiniStat({ label, value, icon: Icon }) {
    return (
        <div className='p-3 rounded-xl border bg-card text-center'>
            <Icon className='w-4 h-4 text-accent mx-auto' />
            <div className='font-semibold text-sm mt-1'>{value}</div>
            <div className='text-[10px] text-muted-foreground'>{label}</div>
        </div>
    );
}

// DownloadAppSection — landing block that shows a QR code + fat CTA button so
// visitors can install the UPPOWER Android app directly from the homepage.
// Version + download URL come from GET /api/downloads/uppower-android-info so
// releasing a new APK auto-updates the CTA copy.
// Bullet list here is deliberately CUSTOMER-FACING (benefits after login),
// NOT the technical release-note highlights returned by the backend — those
// stay tied to the APK auto-update prompt only.
const CUSTOMER_LOGIN_BENEFITS = [
    { emoji: '📊', text: 'PM Surya Ghar application का live status + timeline — कहाँ अटका है, कब मिलेगा subsidy' },
    { emoji: '💰', text: 'Wallet + Referral cashback — हर सफल referral पर ₹500 सीधे wallet में' },
    { emoji: '🏦', text: 'One-tap Loan Apply — SBI/HDFC जैसी banks पर बिना branch जाए apply करें' },
    { emoji: '⚡', text: 'BBPS bill payment — बिजली/mobile/gas — wallet या UPI से, cashback के साथ' },
    { emoji: '🤖', text: 'AI Solar Saathi — Hindi में voice/text पर तुरंत जवाब, EMI + subsidy calculator' },
    { emoji: '🔔', text: 'WhatsApp पर हर milestone update — site survey, approval, installation, meter change' },
];

function DownloadAppSection() {
    const [info, setInfo] = useState(null);
    useEffect(() => {
        const url = `${process.env.REACT_APP_BACKEND_URL}/api/downloads/uppower-android-info`;
        fetch(url).then(r => r.ok ? r.json() : null).then(setInfo).catch(() => setInfo(null));
    }, []);
    const downloadHref = info
        ? `${process.env.REACT_APP_BACKEND_URL}${info.download_url}`
        : `${process.env.REACT_APP_BACKEND_URL}/api/downloads/uppower-android-apk`;
    // Smaller QR request size (200 px) keeps the block visually balanced.
    const qrSrc = `https://api.qrserver.com/v1/create-qr-code/?size=200x200&margin=1&data=${encodeURIComponent(downloadHref)}`;
    const version = info?.version_name || '1.8.0';
    return (
        <section id='download-app' className='max-w-7xl mx-auto px-4 sm:px-8 py-14 scroll-mt-20' data-testid='landing-app-download'>
            <div className='rounded-3xl overflow-hidden border shadow-lg bg-gradient-to-br from-accent/10 via-primary/5 to-emerald-500/10 dark:from-accent/15 dark:via-primary/10 dark:to-emerald-500/15'>
                <div className='grid lg:grid-cols-[1.4fr,1fr] gap-8 p-6 sm:p-10 items-center'>
                    <div>
                        <Badge className='bg-emerald-500/20 text-emerald-800 dark:text-emerald-200 border border-emerald-500/30'>
                            <Smartphone className='w-3 h-3 mr-1' /> Android App · v{version}
                        </Badge>
                        <h2 className='font-display text-3xl sm:text-4xl font-semibold mt-3'>
                            Track your solar journey <br /><span className='text-accent'>from your pocket.</span>
                        </h2>
                        <p className='mt-3 text-sm sm:text-base text-muted-foreground max-w-lg'>
                            UPPOWER Android app में आपको मिलेगा live application tracking, EMI calculator, complaint filing, subsidy status और भी बहुत कुछ — सब कुछ 60-second में install।
                        </p>
                        <ul className='mt-5 space-y-2.5 text-sm' data-testid='landing-app-benefits'>
                            {CUSTOMER_LOGIN_BENEFITS.map((b, i) => (
                                <li key={i} className='flex items-start gap-2.5'>
                                    <span className='shrink-0 text-base leading-tight'>{b.emoji}</span>
                                    <span className='leading-snug'>{b.text}</span>
                                </li>
                            ))}
                        </ul>
                        <div className='mt-6 flex flex-wrap gap-3 items-center'>
                            <Button asChild size='lg' className='min-h-[52px] bg-emerald-600 hover:bg-emerald-700 text-white shadow-lg shadow-emerald-500/20' data-testid='landing-download-apk'>
                                <a href={downloadHref} download>
                                    <Download className='w-5 h-5 mr-2' /> Download UPPOWER v{version}
                                </a>
                            </Button>
                            <div className='text-xs text-muted-foreground'>
                                Free · ~37 MB · Android 6+ <br /> Direct APK install (Play Store not required)
                            </div>
                        </div>
                    </div>
                    <div className='justify-self-center lg:justify-self-end'>
                        <div className='rounded-2xl bg-white p-3 shadow-xl border ring-4 ring-emerald-500/10 relative'>
                            <img src={qrSrc} alt='Scan to install UPPOWER app' className='w-36 h-36 sm:w-44 sm:h-44 block' data-testid='landing-app-qr' />
                            <div className='absolute -bottom-3 left-1/2 -translate-x-1/2 bg-emerald-600 text-white text-[10px] font-bold px-3 py-1 rounded-full shadow whitespace-nowrap'>
                                📱 SCAN TO INSTALL
                            </div>
                        </div>
                        <div className='mt-5 text-center text-[11px] text-muted-foreground max-w-[13rem] mx-auto'>
                            Phone camera खोलें, इस QR पर scan करें, download link तुरंत open हो जाएगा।
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
}


function TestimonialGrid({ items, lang }) {
    if (!items || items.length === 0) return null;
    return (
        <div className='grid sm:grid-cols-3 gap-4'>
            {items.slice(0, 6).map((t, i) => {
                const name = t.name || '';
                const city = t.city?.[lang] || t.city?.en || '';
                const quote = t.quote?.[lang] || t.quote?.en || '';
                const rating = Math.max(1, Math.min(5, Number(t.rating) || 5));
                // Real customer photo when admin has uploaded one, else auto-generated avatar
                const hasRealPhoto = !!(t.photo_url && String(t.photo_url).trim());
                const avatar = hasRealPhoto
                    ? t.photo_url
                    : `https://api.dicebear.com/7.x/notionists/svg?seed=${encodeURIComponent(name || `t-${i}`)}`;
                return (
                    <Card key={t.id || i} className='card-elevate hover:border-primary/40 hover:shadow-lg transition' data-testid={`testimonial-${t.id || i}`}>
                        <CardContent className='p-5'>
                            <div className='flex items-center gap-3'>
                                <img
                                    src={avatar}
                                    alt={name}
                                    className={`w-14 h-14 rounded-full object-cover ${hasRealPhoto ? 'ring-2 ring-emerald-400 shadow-md' : 'border-2 border-primary/20 bg-secondary'}`}
                                    data-testid={`testimonial-avatar-${t.id || i}`}
                                />
                                <div>
                                    <div className='font-semibold flex items-center gap-1'>
                                        {name || '—'}
                                        {hasRealPhoto && <span title='Verified customer' className='text-emerald-600 text-xs'>✓</span>}
                                    </div>
                                    {city && <div className='text-xs text-muted-foreground flex items-center gap-1'><MapPin className='w-3 h-3' /> {city}</div>}
                                </div>
                            </div>
                            {t.kw ? (
                                <div className='mt-4 rounded-lg bg-sky-50 dark:bg-sky-950/30 p-2 text-center'>
                                    <div className='text-[10px] text-muted-foreground uppercase'>System</div>
                                    <div className='font-semibold text-primary'>{t.kw} kW</div>
                                </div>
                            ) : null}
                            {quote && <p className='text-sm mt-4 italic text-muted-foreground'>&ldquo;{quote}&rdquo;</p>}
                            <div className='mt-3 flex items-center gap-0.5 text-amber-500'>
                                {Array.from({ length: rating }).map((_, k) => <Star key={k} className='w-3.5 h-3.5 fill-amber-500' />)}
                            </div>
                        </CardContent>
                    </Card>
                );
            })}
        </div>
    );
}

function StoryGrid({ stories }) {
    // Parse subtitle from body text: "... — City, State · kW · saving"
    const parseStoryMeta = (body) => {
        if (!body) return { quote: '', city: '', kw: '', saving: '' };
        const em = body.split('—');
        const quote = (em[0] || '').trim();
        const meta = (em[1] || '').trim();
        const parts = meta.split('·').map(p => p.trim());
        return { quote, city: parts[0] || '', kw: parts[1] || '', saving: (parts[2] || '').replace(/\/(month|mo)?\s*saving/i, '').trim() };
    };
    if (stories === null) return <div className='text-center text-sm text-muted-foreground py-6'>Loading customer stories…</div>;
    if (stories.length === 0) return <div className='text-center text-sm text-muted-foreground py-6'>Success stories coming soon. Be the first to share yours!</div>;
    return (
        <div className='grid sm:grid-cols-3 gap-4'>
            {stories.slice(0, 6).map(c => {
                const m = parseStoryMeta(c.body_en);
                const img = c.image_base64 || `https://api.dicebear.com/7.x/notionists/svg?seed=${encodeURIComponent(c.title_en || 'Customer')}`;
                return (
                    <Card key={c.id} className='card-elevate hover:border-primary/40 hover:shadow-lg transition' data-testid={`story-card-${c.id}`}>
                        <CardContent className='p-5'>
                            <div className='flex items-center gap-3'>
                                <img src={img} alt={c.title_en} className='w-14 h-14 rounded-full border-2 border-primary/20 bg-secondary object-cover' />
                                <div>
                                    <div className='font-semibold'>{c.title_en}</div>
                                    {m.city && <div className='text-xs text-muted-foreground flex items-center gap-1'><MapPin className='w-3 h-3' /> {m.city}</div>}
                                </div>
                            </div>
                            {(m.kw || m.saving) && (
                                <div className='mt-4 grid grid-cols-2 gap-2'>
                                    {m.kw && <div className='rounded-lg bg-sky-50 dark:bg-sky-950/30 p-2 text-center'>
                                        <div className='text-[10px] text-muted-foreground uppercase'>System</div>
                                        <div className='font-semibold text-primary'>{m.kw}</div>
                                    </div>}
                                    {m.saving && <div className='rounded-lg bg-emerald-50 dark:bg-emerald-950/30 p-2 text-center'>
                                        <div className='text-[10px] text-muted-foreground uppercase'>Saving/month</div>
                                        <div className='font-semibold text-emerald-700'>{m.saving}</div>
                                    </div>}
                                </div>
                            )}
                            {m.quote && <p className='text-sm mt-4 italic text-muted-foreground'>"{m.quote}"</p>}
                            <div className='mt-3 flex items-center gap-0.5 text-amber-500'>
                                {[1, 2, 3, 4, 5].map(i => <Star key={i} className='w-3.5 h-3.5 fill-amber-500' />)}
                            </div>
                        </CardContent>
                    </Card>
                );
            })}
        </div>
    );
}

const FESTIVALS = [
    { key: 'republic', name: 'Republic Day', emoji: '🇮🇳', start: '01-24', end: '01-27', tint: 'from-orange-500 via-white to-green-600', text: 'text-slate-900', msg: 'Wishing India a Happy Republic Day! Get up to ₹78,000 off + free EMI on solar this week.' },
    { key: 'holi', name: 'Holi', emoji: '🎨', start: '03-10', end: '03-18', tint: 'from-pink-500 via-yellow-400 to-emerald-500', text: 'text-white', msg: 'Holi Special: Add rooftop solar this festival week and get FREE meter installation.' },
    { key: 'diwali', name: 'Diwali', emoji: '🪔', start: '10-25', end: '11-05', tint: 'from-yellow-400 via-orange-500 to-rose-600', text: 'text-white', msg: 'Diwali Bumper: Book solar in this festival week — get FREE inverter upgrade worth ₹15,000.' },
];

function activeFestival() {
    const now = new Date();
    const mmdd = String(now.getMonth() + 1).padStart(2, '0') + '-' + String(now.getDate()).padStart(2, '0');
    return FESTIVALS.find(f => mmdd >= f.start && mmdd <= f.end);
}

function FestivalRibbon({ onQuote }) {
    const fest = activeFestival();
    if (!fest) return null;
    return (
        <div className={`bg-gradient-to-r ${fest.tint} ${fest.text} py-2 px-4 text-center text-sm font-medium shadow-inner`} data-testid={`festival-ribbon-${fest.key}`}>
            <span className='mr-2 text-lg'>{fest.emoji}</span>
            <span className='font-semibold'>{fest.name}:</span> {fest.msg}{' '}
            <button className='underline underline-offset-2 font-semibold ml-1' onClick={onQuote}>Claim Offer →</button>
        </div>
    );
}

// ---- Trust Badge Row --------------------------------------------------------
// Slim strip of proof-point chips shown right under the hero to build instant
// credibility (1000+ homes, ISO certified, 5-yr AMC, National Vendor, etc.).
// Each chip is now an anchor link that smooth-scrolls to the relevant proof
// section (testimonials, bank partners, Solar Saathi, etc.) so visitors can
// dig deeper with a single tap.
function TrustBadgeRow({ t }) {
    const items = [
        { icon: HomeIcon, label: '1000+ Happy Homes', href: '#stories', tint: 'text-emerald-700 dark:text-emerald-400', bg: 'bg-emerald-50/70 dark:bg-emerald-950/40 ring-emerald-200/70 dark:ring-emerald-900/50 hover:bg-emerald-100 dark:hover:bg-emerald-900/60' },
        { icon: ShieldCheck, label: 'ISO 9001 Certified', href: '#about', tint: 'text-sky-700 dark:text-sky-400', bg: 'bg-sky-50/70 dark:bg-sky-950/40 ring-sky-200/70 dark:ring-sky-900/50 hover:bg-sky-100 dark:hover:bg-sky-900/60' },
        { icon: Wrench, label: '5-year Free AMC', href: '#offering', tint: 'text-amber-700 dark:text-amber-400', bg: 'bg-amber-50/70 dark:bg-amber-950/40 ring-amber-200/70 dark:ring-amber-900/50 hover:bg-amber-100 dark:hover:bg-amber-900/60' },
        { icon: Award, label: 'National Solar Vendor', href: '#about', tint: 'text-violet-700 dark:text-violet-400', bg: 'bg-violet-50/70 dark:bg-violet-950/40 ring-violet-200/70 dark:ring-violet-900/50 hover:bg-violet-100 dark:hover:bg-violet-900/60' },
        { icon: Landmark, label: '6 Bank Tie-ups', href: '#bank-partners', tint: 'text-indigo-700 dark:text-indigo-400', bg: 'bg-indigo-50/70 dark:bg-indigo-950/40 ring-indigo-200/70 dark:ring-indigo-900/50 hover:bg-indigo-100 dark:hover:bg-indigo-900/60' },
        { icon: HeartHandshake, label: '24×7 Solar Saathi', href: '#solar-saathi', tint: 'text-rose-700 dark:text-rose-400', bg: 'bg-rose-50/70 dark:bg-rose-950/40 ring-rose-200/70 dark:ring-rose-900/50 hover:bg-rose-100 dark:hover:bg-rose-900/60' },
    ];

    const scrollTo = (e, href) => {
        try {
            e.preventDefault();
            const el = document.querySelector(href);
            if (!el) return;
            // Offset for the sticky header (~64px) so the heading isn't hidden.
            const y = el.getBoundingClientRect().top + window.scrollY - 72;
            window.scrollTo({ top: y, behavior: 'smooth' });
            // Keep the URL hash so users can share the deep link.
            if (window.history && window.history.replaceState) {
                window.history.replaceState(null, '', href);
            }
        } catch { /* ignore */ }
    };

    return (
        <section
            className='border-y border-border/60 bg-muted/30 dark:bg-muted/10'
            data-testid='trust-badge-row'
            aria-label='Trust and credibility'
        >
            <div className='max-w-7xl mx-auto px-4 sm:px-8 py-3'>
                <div
                    className='flex items-center gap-2 sm:gap-3 flex-nowrap overflow-x-auto -mx-1 px-1'
                    style={{ scrollbarWidth: 'none' }}
                >
                    {items.map((it, i) => {
                        const Icon = it.icon;
                        return (
                            <a
                                key={i}
                                href={it.href}
                                onClick={(e) => scrollTo(e, it.href)}
                                className={`shrink-0 group inline-flex items-center gap-1.5 rounded-full px-3 py-1.5 ring-1 ${it.bg} text-xs sm:text-sm font-medium whitespace-nowrap transition-colors duration-150 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-primary/60`}
                                data-testid={`trust-chip-${i}`}
                                aria-label={`${it.label} — jump to section`}
                            >
                                <Icon className={`w-3.5 h-3.5 ${it.tint} shrink-0`} />
                                <span className={it.tint}>{it.label}</span>
                                <ArrowRight className={`w-3 h-3 ${it.tint} opacity-0 -ml-0.5 -translate-x-1 group-hover:opacity-100 group-hover:translate-x-0 group-focus-visible:opacity-100 group-focus-visible:translate-x-0 transition-all duration-150`} />
                            </a>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}


function SavingsSlider({ onQuote }) {
    const [kw, setKw] = useState(3);
    const [rates, setRates] = useState([]);
    useEffect(() => { api.subsidyRates().then(setRates).catch(() => setRates([])); }, []);
    // Assumptions: 4 units/day/kW average, ₹7.5/unit tariff
    const unitsPerMonth = Math.round(kw * 4 * 30);
    const monthlySave = Math.round(unitsPerMonth * 7.5);
    const yearlySave = monthlySave * 12;
    // Subsidy from backend rate chart (matches EMI calc & OnGrid Rate Chart).
    // Fallback slab (Central+State combined for UP) when API is loading: 1kW=45k, 2kW=90k, ≥3kW=1,08,000.
    const subsidy = useMemo(() => {
        if (!rates?.length) return kw <= 1 ? 45000 : kw <= 2 ? 90000 : 108000;
        const sorted = [...rates].sort((a, b) => a.kw - b.kw);
        let picked = sorted[0];
        for (const r of sorted) if (r.kw <= kw) picked = r;
        return (picked.central_subsidy || 0) + (picked.state_subsidy || 0);
    }, [rates, kw]);
    // System cost from admin's rate chart when configured; otherwise fall back
    // to the public UPPOWER slab-based rate chart (see systemCostForKw util).
    const grossCost = useMemo(() => {
        if (!rates?.length) return systemCostForKw(kw);
        const sorted = [...rates].sort((a, b) => a.kw - b.kw);
        let picked = sorted[0];
        for (const r of sorted) if (r.kw <= kw) picked = r;
        return picked.system_cost || systemCostForKw(kw);
    }, [rates, kw]);
    const netCost = Math.max(0, grossCost - subsidy);
    const paybackYears = monthlySave > 0 ? Math.max(1, (netCost / yearlySave)) : 0;
    // 25-year lifetime savings vs grid: assume ~15% panel derating averaged
    // over lifetime, minus customer's out-of-pocket cost. Universal industry rule-of-thumb.
    const lifetime25 = Math.max(0, Math.round(yearlySave * 25 * 0.85 - netCost));
    // Approx rooftop area required — India standard ~100 sq ft (≈10 sqm) per 1 kW of installed panels
    const areaSqFt = kw * 100;
    const areaSqM = kw * 10;
    return (
        <div className='rounded-2xl border border-primary/20 bg-card shadow-lg overflow-hidden' data-testid='savings-slider-card'>
            {/* Header strip — kW selector + live price */}
            <div className='grid grid-cols-1 md:grid-cols-[1.4fr,1fr] gap-0'>
                {/* LEFT — slider column */}
                <div className='p-5 sm:p-6 space-y-3 border-b md:border-b-0 md:border-r border-border/60'>
                    <div className='flex items-baseline justify-between'>
                        <div>
                            <label className='text-[11px] uppercase tracking-wide text-muted-foreground font-semibold'>Rooftop system size</label>
                            <div className='text-3xl font-display font-bold text-primary leading-tight'>{kw} kW</div>
                        </div>
                        <div className='text-right'>
                            <div className='text-[11px] uppercase tracking-wide text-muted-foreground font-semibold'>Base rate</div>
                            <div className='text-sm font-mono font-semibold text-slate-700 dark:text-slate-200'>
                                ₹{Math.round(grossCost / kw).toLocaleString('en-IN')}/kW
                            </div>
                        </div>
                    </div>
                    <input
                        type='range'
                        min='1'
                        max='15'
                        step='1'
                        value={kw}
                        onChange={e => setKw(Number(e.target.value))}
                        className='w-full h-2 bg-secondary rounded-full appearance-none cursor-pointer accent-primary'
                        data-testid='savings-slider'
                    />
                    <div className='flex justify-between text-[10px] text-muted-foreground px-0.5'>
                        <span>1 kW</span><span>5 kW</span><span>10 kW</span><span>15 kW</span>
                    </div>
                    <div className='rounded-lg border border-dashed border-primary/30 bg-primary/5 px-3 py-2 flex items-center gap-2 flex-wrap' data-testid='ss-area'>
                        <HomeIcon className='w-4 h-4 text-primary shrink-0' />
                        <span className='text-xs'>
                            <span className='text-muted-foreground'>Rooftop area / छत क्षेत्रफल: </span>
                            <b className='text-primary'>~{areaSqFt.toLocaleString('en-IN')} sq ft</b>
                            <span className='text-muted-foreground'> ({areaSqM} m²)</span>
                        </span>
                    </div>
                </div>

                {/* RIGHT — price summary column */}
                <div className='p-5 sm:p-6 bg-gradient-to-br from-primary/5 via-transparent to-emerald-500/5 space-y-3'>
                    <div>
                        <div className='text-[11px] uppercase tracking-wide text-muted-foreground font-semibold'>Your Investment</div>
                        <div className='mt-1 flex items-baseline gap-2 flex-wrap'>
                            <span className='text-3xl font-display font-bold text-primary tabular-nums'>
                                ₹{netCost.toLocaleString('en-IN')}
                            </span>
                            <span className='text-sm text-muted-foreground line-through tabular-nums'>
                                ₹{grossCost.toLocaleString('en-IN')}
                            </span>
                        </div>
                        <div className='text-[11px] text-emerald-700 dark:text-emerald-400 font-semibold mt-0.5'>
                            After ₹{subsidy.toLocaleString('en-IN')} PM Surya Ghar subsidy
                        </div>
                    </div>
                    <div className='pt-3 border-t border-border/60 grid grid-cols-2 gap-3'>
                        <div>
                            <div className='text-[10px] uppercase text-muted-foreground'>Payback</div>
                            <div className='font-display font-bold text-lg text-slate-700 dark:text-slate-200'>{paybackYears.toFixed(1)} yrs</div>
                        </div>
                        <div>
                            <div className='text-[10px] uppercase text-muted-foreground'>25-yr Savings</div>
                            <div className='font-display font-bold text-lg text-emerald-700 dark:text-emerald-400'>₹{Math.round(lifetime25 / 100000)}L+</div>
                        </div>
                    </div>
                </div>
            </div>

            {/* Bottom — savings breakdown strip */}
            <div className='grid grid-cols-2 sm:grid-cols-4 border-t border-border/60'>
                <EstimatorStat label='Monthly Saving' value={`₹${monthlySave.toLocaleString('en-IN')}`} tone='emerald' testId='ss-monthly' />
                <EstimatorStat label='Yearly Saving' value={`₹${yearlySave.toLocaleString('en-IN')}`} tone='sky' testId='ss-yearly' />
                <EstimatorStat label='PM Surya Ghar Subsidy' value={`₹${subsidy.toLocaleString('en-IN')}`} tone='amber' testId='ss-subsidy' />
                <EstimatorStat label='Gross System Cost' value={`₹${grossCost.toLocaleString('en-IN')}`} tone='slate' testId='ss-gross' />
            </div>

            {/* CTA */}
            <div className='p-5 sm:p-6 pt-4 border-t border-border/60 bg-muted/20'>
                <Button className='w-full min-h-[48px] bg-accent hover:bg-accent/90 text-accent-foreground font-semibold' onClick={() => onQuote(kw)} data-testid='savings-cta'>
                    Get exact quote for my {kw} kW system <ArrowRight className='ml-2 w-4 h-4' />
                </Button>
                <p className='text-[10px] text-center text-muted-foreground mt-2'>* Indicative estimate based on UP average tariff & PM Surya Ghar 2026 rates. Actual varies by DISCOM, orientation & appliances.</p>
            </div>
        </div>
    );
}

// Compact stat used inside the estimator's bottom strip
function EstimatorStat({ label, value, tone, testId }) {
    const tones = {
        emerald: 'text-emerald-700 dark:text-emerald-300',
        sky: 'text-sky-700 dark:text-sky-300',
        amber: 'text-amber-700 dark:text-amber-300',
        slate: 'text-slate-700 dark:text-slate-300',
    };
    return (
        <div className='p-3 sm:p-4 text-center border-r border-b sm:border-b-0 border-border/60 last:border-r-0 nth-3:sm:border-r-0' data-testid={testId}>
            <div className='text-[10px] uppercase tracking-wide text-muted-foreground font-semibold'>{label}</div>
            <div className={`font-display font-bold text-base sm:text-lg mt-0.5 tabular-nums ${tones[tone]}`}>{value}</div>
        </div>
    );
}

function Stat({ label, value, tone, testId }) {
    const tones = {
        emerald: 'bg-emerald-50 text-emerald-700 dark:bg-emerald-950/30 dark:text-emerald-300',
        sky: 'bg-sky-50 text-sky-700 dark:bg-sky-950/30 dark:text-sky-300',
        amber: 'bg-amber-50 text-amber-700 dark:bg-amber-950/30 dark:text-amber-300',
        slate: 'bg-slate-50 text-slate-700 dark:bg-slate-950/30 dark:text-slate-300',
    };
    return (
        <div className={`rounded-xl p-3 ${tones[tone]}`} data-testid={testId}>
            <div className='text-[10px] uppercase opacity-80'>{label}</div>
            <div className='font-bold text-lg mt-0.5'>{value}</div>
        </div>
    );
}

function SaathiBookingDialog({ open, onClose, t }) {
    const emptyForm = { name: '', mobile: '', email: '', address: '', city: '', pincode: '', preferred_date: '', preferred_slot: 'Morning', notes: '' };
    const [f, setF] = useState(emptyForm);
    const [busy, setBusy] = useState(false);
    // Phase 36 — OTP gate.
    const [otpRequired, setOtpRequired] = useState(false);
    const [otpToken, setOtpToken] = useState('');
    useEffect(() => {
        if (!open) return;
        setOtpToken('');
        extraApi.publicLeadOtpRequired()
            .then(r => setOtpRequired(!!r?.public_lead_otp_required))
            .catch(() => setOtpRequired(false));
    }, [open]);
    const submit = async () => {
        if (!f.name?.trim() || !f.mobile?.trim() || f.mobile.length < 10) {
            toast.error('Name and 10-digit mobile required');
            return;
        }
        if (otpRequired && !otpToken) {
            toast.error('कृपया पहले mobile OTP verify करें');
            return;
        }
        setBusy(true);
        try {
            const res = await extraApi.postSaathiBooking({ ...f, otp_token: otpToken || undefined });
            toast.success(res?.message || 'Solar Saathi will call you shortly!');
            onClose();
            setF(emptyForm);
            setOtpToken('');
        } catch (e) {
            toast.error(e?.response?.data?.detail || 'Failed to submit');
        } finally { setBusy(false); }
    };
    // Min date = today
    const today = new Date().toISOString().slice(0, 10);
    return (
        <Dialog open={open} onOpenChange={o => !o && onClose()}>
            <DialogContent className='max-w-lg' data-testid='saathi-booking-dialog'>
                <DialogHeader>
                    <DialogTitle className='flex items-center gap-2'>
                        <span className='w-8 h-8 rounded-lg bg-gradient-to-br from-amber-500 to-orange-600 text-white grid place-items-center'>
                            <HeartHandshake className='w-4 h-4' />
                        </span>
                        {t('saathi_book_title') || 'Book a Solar Saathi Home Visit'}
                    </DialogTitle>
                </DialogHeader>
                <p className='text-xs text-muted-foreground -mt-2'>{t('saathi_book_sub') || 'Our Solar Saathi will visit your home for a free rooftop survey and guide you through the subsidy process.'}</p>
                <div className='grid grid-cols-2 gap-3'>
                    <div className='space-y-1 col-span-2'>
                        <Label>{t('quote_your_name')} *</Label>
                        <Input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} data-testid='saathi-name' />
                    </div>
                    <div className='space-y-1'>
                        <Label>{t('quote_your_mobile')} *</Label>
                        <Input inputMode='numeric' value={f.mobile} onChange={e => setF({ ...f, mobile: e.target.value.replace(/\D/g, '').slice(0, 10) })} data-testid='saathi-mobile' />
                    </div>
                    <div className='space-y-1'>
                        <Label>{t('email')}</Label>
                        <Input type='email' value={f.email} onChange={e => setF({ ...f, email: e.target.value })} data-testid='saathi-email' />
                    </div>
                    <div className='space-y-1 col-span-2'>
                        <Label>{t('saathi_book_address') || 'Address'}</Label>
                        <Textarea rows={2} value={f.address} onChange={e => setF({ ...f, address: e.target.value })} placeholder='House no, street, area' data-testid='saathi-address' />
                    </div>
                    <div className='space-y-1'>
                        <Label>{t('city')}</Label>
                        <Input value={f.city} onChange={e => setF({ ...f, city: e.target.value })} data-testid='saathi-city' />
                    </div>
                    <div className='space-y-1'>
                        <Label>Pincode</Label>
                        <Input inputMode='numeric' value={f.pincode} onChange={e => setF({ ...f, pincode: e.target.value.replace(/\D/g, '').slice(0, 6) })} data-testid='saathi-pincode' />
                    </div>
                    <div className='space-y-1'>
                        <Label>{t('saathi_book_date') || 'Preferred date'}</Label>
                        <Input type='date' min={today} value={f.preferred_date} onChange={e => setF({ ...f, preferred_date: e.target.value })} data-testid='saathi-date' />
                    </div>
                    <div className='space-y-1'>
                        <Label>{t('saathi_book_slot') || 'Preferred slot'}</Label>
                        <Select value={f.preferred_slot} onValueChange={v => setF({ ...f, preferred_slot: v })}>
                            <SelectTrigger data-testid='saathi-slot'><SelectValue /></SelectTrigger>
                            <SelectContent>
                                <SelectItem value='Morning'>{t('saathi_slot_morning') || 'Morning (9 – 12)'}</SelectItem>
                                <SelectItem value='Afternoon'>{t('saathi_slot_afternoon') || 'Afternoon (12 – 4)'}</SelectItem>
                                <SelectItem value='Evening'>{t('saathi_slot_evening') || 'Evening (4 – 7)'}</SelectItem>
                            </SelectContent>
                        </Select>
                    </div>
                    <div className='space-y-1 col-span-2'>
                        <Label>{t('quote_message')}</Label>
                        <Textarea rows={2} value={f.notes} onChange={e => setF({ ...f, notes: e.target.value })} placeholder='Any specific requirement?' data-testid='saathi-notes' />
                    </div>
                    <OtpVerifyBlock
                        required={otpRequired}
                        mobile={f.mobile}
                        purpose='saathi'
                        onVerified={setOtpToken}
                        testId='saathi-otp'
                    />
                </div>
                <DialogFooter>
                    <Button variant='outline' onClick={onClose} data-testid='saathi-cancel'>{t('cancel') || 'Cancel'}</Button>
                    <Button className='bg-amber-600 hover:bg-amber-700 text-white' onClick={submit} disabled={busy || (otpRequired && !otpToken)} data-testid='saathi-submit'>
                        {busy ? 'Submitting…' : (t('saathi_book_submit') || 'Confirm Home Visit')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}
